import { AppStoreProvider, useAppStoreApi } from "../state/app-store-context";
import type { Locale } from "@src/i18n/locale-catalog";
import type { Translate } from "@src/i18n/translate";
import { useMemo, useRef, useState } from "react";
import type { AppDialog } from "./AppDialogs";
import { AppDialogs } from "./AppDialogs";
import type { AppStore } from "../state/app-store";
import { EquationEditor } from "./EquationEditor";
import { PlaybackControls } from "./PlaybackControls";
import { AppControls } from "./AppControls";
import { TransformationViewport } from "@src/features/visualization/TransformationViewport";
import type { TransformationViewportHandle } from "@src/features/visualization/TransformationViewport";
import { createAppStore } from "../state/app-store";
import { createVisualizationSource } from "../visualization-source";
import { translate } from "@src/i18n/translate";
import { useShareController } from "../hooks/use-share-controller";

export function App({
  locale,
  t,
  version,
  store,
}: {
  locale: Locale;
  t: Translate;
  version: string;
  store?: AppStore;
}) {
  const [appStore] = useState(() => store ?? createAppStore());
  return (
    <AppStoreProvider store={appStore}>
      <AppContent locale={locale} t={t} version={version} />
    </AppStoreProvider>
  );
}

function AppContent({
  locale,
  t,
  version,
}: {
  locale: Locale;
  t: Translate;
  version: string;
}) {
  const appStore = useAppStoreApi();
  const scene = useRef<TransformationViewportHandle>(null);
  const visualizationSource = useMemo(
    () => createVisualizationSource(appStore),
    [appStore],
  );
  const [dialogInvoker, setDialogInvoker] = useState<{
    dialog: Exclude<AppDialog, null>;
    element: HTMLElement;
  } | null>(null);
  const [visualizationReady, setVisualizationReady] = useState(false);
  const [dialog, setDialog] = useState<AppDialog>(null);
  const [activeLocale, setActiveLocale] = useState(locale);
  const activeT = useMemo<Translate>(
    () =>
      activeLocale === locale
        ? t
        : (key, values) => translate(activeLocale, key, values),
    [activeLocale, locale, t],
  );
  const share = useShareController({
    locale: activeLocale,
    scene,
    setDialog,
    onLocaleChange: setActiveLocale,
  });

  return (
    <main
      className="shell"
      inert={!share.ready || undefined}
      aria-busy={!share.ready || undefined}
    >
      <section
        className="stage"
        aria-label={activeT("transformationViewport")}
        inert={share.isSharing || undefined}
        aria-busy={share.isSharing || undefined}
      >
        <TransformationViewport
          ref={scene}
          source={visualizationSource}
          t={activeT}
          onAvailabilityChange={setVisualizationReady}
        />
        <AppControls
          locale={activeLocale}
          t={activeT}
          scene={scene}
          shareStatus={share.shareStatus}
          isSharing={share.isSharing}
          onChangeLocale={share.changeLocale}
          onShare={(invoker) => {
            setDialogInvoker({
              dialog: "share",
              element: invoker,
            });
            void share.share();
          }}
          onDialog={(nextDialog, invoker) => {
            setDialogInvoker(
              nextDialog
                ? {
                    dialog: nextDialog,
                    element: invoker,
                  }
                : null,
            );
            setDialog(nextDialog);
          }}
        />
      </section>
      <footer
        className="panel"
        aria-label={activeT("controls")}
        inert={share.isSharing || undefined}
        aria-busy={share.isSharing || undefined}
      >
        <PlaybackControls t={activeT} visualizationReady={visualizationReady} />
        <EquationEditor t={activeT} />
      </footer>
      <span
        className="visually-hidden"
        data-share-status
        role="status"
        aria-live="polite"
      >
        {share.shareStatus ? activeT(share.shareStatus) : ""}
      </span>
      <AppDialogs
        dialog={dialog}
        setDialog={setDialog}
        t={activeT}
        version={version}
        shareLink={share.shareLink}
        shareCopyOutcome={share.shareCopyOutcome}
        returnFocus={
          dialogInvoker?.dialog === dialog ? dialogInvoker.element : null
        }
        onReset={share.resetApp}
        onRecoverInvalidShare={share.recoverInvalidShare}
      />
    </main>
  );
}
